import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { PageHeader, Spinner } from "@/components/common/Common";
import { ContractEmailDialog, textToHtml } from "@/components/email/ContractEmailDialog";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate } from "@/lib/api";
import { DEACTIVATION_REASONS } from "@/lib/constants";

function Tile({ t }) {
  return (
    <Link to={`/contracts?queue=${t.key}`} data-testid={`queue-tile-${t.key}`}
      className={`group bg-white border rounded-lg p-4 shadow-sm hover:shadow-md hover:border-blue-300 transition-all ${t.count ? "border-slate-200" : "border-slate-100 opacity-70"}`}>
      <div className="text-3xl font-display font-bold text-slate-900 group-hover:text-blue-700" data-testid={`queue-count-${t.key}`}>{t.count}</div>
      <div className="text-xs text-slate-500 mt-1 leading-snug">{t.label}</div>
    </Link>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-2xl font-display font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function MyOverdue({ items, reload }) {
  const { canWrite } = useAuth();
  const [chase, setChase] = useState(null);
  const open = async (it) => {
    const { data } = await api.get(`/contracts/${it.contract.id}/checklist/${it.id}/chase`);
    setChase({ contract: it.contract, draft: { ...data, html: textToHtml(data.body) } });
  };
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="my-overdue-panel">
      <h3 className="text-lg font-semibold text-slate-800 mb-3">My overdue items <span className="text-sm font-normal text-slate-400">({items.length})</span></h3>
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`my-overdue-${it.id}`}>
            <Link to={`/contracts/${it.contract.id}`} className="min-w-0 flex-1 hover:text-blue-700">
              <p className="text-sm text-slate-800 truncate">{it.label}</p>
              <p className="text-xs text-slate-500"><span className="font-mono">{it.contract.employment_id}</span> · {it.contract.contractor_name} · requested {fmtDate(it.requested_at)} ({it.days} days)</p>
            </Link>
            {canWrite && <Button size="sm" variant="outline" className="text-amber-800 border-amber-300 bg-amber-50 hover:bg-amber-100" onClick={() => open(it)} data-testid={`my-overdue-chase-${it.id}`}><BellRing className="h-3.5 w-3.5 mr-1" />Chase</Button>}
          </li>
        ))}
        {!items.length && <li className="py-3 text-sm text-slate-400">Nothing overdue on your contracts. Nice work.</li>}
      </ul>
      {chase && <ContractEmailDialog contract={chase.contract} initial={chase.draft} onClose={() => setChase(null)} onSent={reload} />}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard/overview").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="flex justify-center p-12"><Spinner /></div>;
  const k = d.kpis;
  const chart = k.by_month.map((m) => ({ ...m, label: `${m.month.slice(5)}/${m.month.slice(2, 4)}` }));
  return (
    <div className="space-y-6">
      <PageHeader title={`G'day, ${user.full_name.split(" ")[0]}`} subtitle="Live counts. Click a tile to open the matching contracts. The dashboard never sends anything." testId="dashboard-header" />
      <MyOverdue items={d.my_overdue} reload={load} />
      {d.groups.map((g) => (
        <section key={g.name} data-testid={`queue-group-${g.name.toLowerCase().replace(" ", "-")}`}>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">{g.name}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">{g.tiles.map((t) => <Tile key={t.key} t={t} />)}</div>
        </section>
      ))}
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Key numbers</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
          <Kpi label="Active · PayMe Australia" value={k.active_payme} testId="kpi-active-payme" />
          <Kpi label="Active · PTE company" value={k.active_pte} testId="kpi-active-pte" />
          <Kpi label="New joins this month" value={k.new_joins_month} testId="kpi-joins-month" />
          <Kpi label="New joins this year" value={k.new_joins_year} testId="kpi-joins-year" />
          <Kpi label="Deactivations this month" value={k.deactivations_month} testId="kpi-deact-month" />
          <Kpi label="Deactivations last month" value={k.deactivations_last_month} testId="kpi-deact-last-month" />
          <Kpi label="Deactivations this year" value={k.deactivations_year} testId="kpi-deact-year" />
        </div>
      </section>
      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="joins-chart">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">New joins vs deactivations · last 24 months</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={1} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={28} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="new_joins" name="New joins" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Bar dataKey="deactivations" name="Deactivations" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="deactivation-reasons">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Deactivations by reason</h3>
          <ul className="space-y-2">
            {k.deactivations_by_reason.map((r) => {
              const max = Math.max(...k.deactivations_by_reason.map((x) => x.count));
              return (
                <li key={r.reason || "none"} className="text-sm">
                  <div className="flex justify-between"><span className="text-slate-700">{DEACTIVATION_REASONS[r.reason] || "No reason"}</span><span className="font-mono text-slate-500">{r.count}</span></div>
                  <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-1.5 bg-amber-500 rounded-full" style={{ width: `${(r.count / max) * 100}%` }} /></div>
                </li>
              );
            })}
            {!k.deactivations_by_reason.length && <li className="text-sm text-slate-400">No deactivations yet</li>}
          </ul>
        </section>
      </div>
    </div>
  );
}
